import { PageOverviewBanner } from '@/components';
import Adsense from '@/components/Adsense';
import { AdsenseSectionWrapper } from '@/components/Adsense/Adsense.styles';
import SEOSection from '@/components/SEOSection';
import {
  CarrierInformation,
  CustomerReviews,
  FaqAccordion,
  HowTrackingWorks,
  Leaderboard,
  PartnerLogos,
  useTrackingPageInsights,
} from '@/components/TrackingInsights';
import { WrapperSectionContainer } from '@/components/Wrapper/Wrapper.styles';
import { seoPageAdSlots } from '@/constants';
import {
  type ButtonProps,
  type ILandingPage,
  SectionThemeKey,
  SeoPageAdType,
  Widget,
  WrapperBackgroundColor,
} from '@/interfaces';
import { extractCarrierSlug } from '@/utils';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import SectionListRenderer from '../SectionListRenderer';

const TRACKING_SOLUTION_ICONS = [
  '/images/icons/tracking-solution-1.svg',
  '/images/icons/tracking-solution-2.svg',
  '/images/icons/tracking-solution-3.svg',
  '/images/icons/tracking-solution-4.svg',
];

const DEFAULT_OVERVIEW_BANNER_HERO_ASSET = {
  url: '/images/overview_banner.png',
  contentType: 'image/png',
  description: 'Overview banner',
};

interface AdsenseWrapperProps {
  $bgColor?: WrapperBackgroundColor;
}

/**
 * Carrier pages put the CMS-driven "Join 1m+ users" stats band on `surface02`, which
 * `WrapperBackgroundColor` (white / grey `pageBackground`) cannot express, so the wrapper's
 * background is overridden here the same way the ad slots are.
 */
const StatsBand = styled.div<{ $tinted?: boolean }>(({ theme, $tinted }) => ({
  ...($tinted ? { [`${WrapperSectionContainer}`]: { background: theme.colors.surface02 } } : {}),
}));

const AdsenseWrapper = styled.div<AdsenseWrapperProps>`
  ${({ theme, $bgColor }) => `
    ${AdsenseSectionWrapper} {
      background: ${
        $bgColor === WrapperBackgroundColor.grey ? theme.colors.pageBackground : theme.colors.highEmphasisWhite
      };
    }
  `}
`;

interface SEOPageRendererProps {
  data: ILandingPage;
  /** Localized country / region name, resolved on the server — see `getSeoPagePlaceName`. */
  placeName?: string | null;
}

const SEOPageRenderer: React.FC<SEOPageRendererProps> = ({ data, placeName }) => {
  const { pageOverviewBanner, pageOverviewBannerCtaButtons, pageOverviewBannerHeroImage, faq, trackingSolution } = data;
  const router = useRouter();
  const { slug } = router.query as { slug: string };
  const carrierSlug = extractCarrierSlug(data, slug);
  const { insights, isCarrierPage, partnerLogos, topCarriersTitle, topMerchantsTitle, contentSections } =
    useTrackingPageInsights(data, placeName);

  const filteredTrackingSeoList =
    trackingSolution?.seoList
      ?.filter((item) => !!item?.title)
      .map((item, i) => ({
        ...item,
        icon: { url: TRACKING_SOLUTION_ICONS[i], contentType: 'image/svg+xml' },
      })) ?? [];

  const shouldRenderFaq = faq?.title && faq.content;
  const shouldRenderTrackingSolution = trackingSolution?.title && filteredTrackingSeoList.length > 0;

  return (
    <>
      {pageOverviewBanner && (
        <PageOverviewBanner
          widget={Widget.Tracking}
          title={pageOverviewBanner.title}
          titleMobile={pageOverviewBanner.titleMobile ?? pageOverviewBanner.title}
          subTitle={pageOverviewBanner.subTitle}
          subText={pageOverviewBanner.subText}
          subTextMobile={pageOverviewBanner.subTextMobile}
          buttons={pageOverviewBannerCtaButtons as ButtonProps[]}
          theme={pageOverviewBanner.theme ?? SectionThemeKey.Dark}
          heroAsset={pageOverviewBannerHeroImage ?? DEFAULT_OVERVIEW_BANNER_HERO_ASSET}
          carrierSlug={carrierSlug}
          priority
        />
      )}
      <AdsenseWrapper $bgColor={contentSections[0]?.backgroundColor as WrapperBackgroundColor}>
        <Adsense className='seo-ads seo-ads-top' dataAdSlot={seoPageAdSlots[SeoPageAdType.TOP]} />
      </AdsenseWrapper>

      {partnerLogos.length > 0 && <PartnerLogos logos={partnerLogos} />}

      {insights?.carrierInformation && <CarrierInformation information={insights.carrierInformation} />}

      <StatsBand $tinted={isCarrierPage}>
        <SectionListRenderer sectionList={contentSections} />
      </StatsBand>

      <AdsenseWrapper $bgColor={WrapperBackgroundColor.white}>
        <Adsense className='seo-ads seo-ads-middle' dataAdSlot={seoPageAdSlots[SeoPageAdType.MIDDLE]} />
      </AdsenseWrapper>
      {insights?.topCarriers?.length ? <Leaderboard title={topCarriersTitle} entries={insights.topCarriers} /> : null}

      {insights?.topMerchants?.length ? (
        // Tinted to set it off from the white carriers board above; carrier pages have no carriers
        // board, and the dark EMS banner already separates it from the stats band.
        <Leaderboard title={topMerchantsTitle} entries={insights.topMerchants} tinted={!isCarrierPage} />
      ) : null}

      {insights?.howTrackingWorks && (
        <HowTrackingWorks content={insights.howTrackingWorks} carrierName={insights.carrierInformation?.name} />
      )}

      {shouldRenderFaq && <FaqAccordion title={faq.title as string} content={faq.content as ContentfulDocument} />}
      <AdsenseWrapper $bgColor={WrapperBackgroundColor.white}>
        <Adsense className='seo-ads seo-ads-bottom' dataAdSlot={seoPageAdSlots[SeoPageAdType.BOTTOM]} />
      </AdsenseWrapper>

      {insights?.reviews?.length ? <CustomerReviews reviews={insights.reviews} tinted /> : null}

      {shouldRenderTrackingSolution && (
        <SEOSection
          title={trackingSolution.title as string}
          backgroundColor={WrapperBackgroundColor.grey}
          seoList={filteredTrackingSeoList}
        />
      )}
    </>
  );
};

export default SEOPageRenderer;
